import { memo } from "react";
import type { RatingWithEffectiveDebits } from "../../types";
import { Alert } from "../Alert";
import { TagPill } from "../Pills";
import { CheckboxInput } from "../fields/CheckboxInput";
import { NumberInput } from "../fields/NumberInput";
import { NoteEditor } from "../member/NoteEditor";

import { EMPTY_SELECTION_DRAFT, type RatingSelectionDraft } from "./ratingSelection";

interface RatingsTableProps {
  ratings: readonly RatingWithEffectiveDebits[];
  selections: ReadonlyMap<number, RatingSelectionDraft>;
  /** Ratings already on the member: shown, but not selectable here; they are edited or removed on the member screen. */
  assignedRatingIds: ReadonlySet<number>;
  onToggle: (rating: RatingWithEffectiveDebits, checked: boolean) => void;
  onDraftChange: (ratingId: number, draft: RatingSelectionDraft) => void;
  disabled?: boolean;
  children?: never;
}

function modificationTitle(rating: RatingWithEffectiveDebits): string | undefined {
  if (!rating.modification) return undefined;
  const rule = rating.modification.overrideDebits !== null ? `override ${rating.modification.overrideDebits}` : `multiplier ${rating.modification.multiplier}x`;
  return `Base ${rating.baseDebits}, ${rule}. ${rating.modification.notes}`;
}

const HEAD = "px-2 py-2 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-gray-600";
const ON_MEMBER_TOOLTIP = "Already on this member. Edit or remove it on the member screen.";

/**
 * Ratings in stored order. Every row carries its own override, UW Dollars, IRIX
 * Source, and Notes controls on one line, enabled once the row is checked, so
 * nothing expands and the whole picker stays scannable. The note opens the same
 * popover as the member grid and lands in the row's draft when the popover closes,
 * so typing re-renders the textarea alone, not 300 rows.
 */
export function RatingsTable({ ratings, selections, assignedRatingIds, onToggle, onDraftChange, disabled }: RatingsTableProps) {
  const substitutions = ratings.filter((rating) => selections.has(rating.ratingId) && rating.genericSubstitute);

  return (
    <div className="max-xl:overflow-x-auto">
      {substitutions.map((rating) => (
        <Alert key={rating.ratingId} type="info" showIcon message={`Debits loaded from generic ${rating.genericSubstitute?.ratingText.replace("Rx : ", "")} (${rating.genericSubstitute?.effectiveDebits}) for ${rating.ratingText.replace("Rx : ", "")}.`} className="mb-3" />
      ))}
      <table className="w-full text-[13px]">
        <caption className="sr-only">Ratings for this condition</caption>
        <thead className="sticky top-16 z-10 bg-white">
          <tr className="border-b border-gray-200">
            <th scope="col" className={`${HEAD} w-10`}>
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" className={HEAD}>Rating</th>
            <th scope="col" className={`${HEAD} w-24`}>Timing</th>
            <th scope="col" className={`${HEAD} w-16 text-right`}>Debits</th>
            <th scope="col" className={`${HEAD} w-24`}>Override</th>
            <th scope="col" className={`${HEAD} w-24`}>UW $</th>
            <th scope="col" className={`${HEAD} w-14 text-center`}>IRIX</th>
            <th scope="col" className={`${HEAD} w-52`}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {ratings.map((rating) => (
            <RatingRow key={rating.ratingId} rating={rating} draft={selections.get(rating.ratingId)} isOnMember={assignedRatingIds.has(rating.ratingId)} disabled={disabled} onToggle={onToggle} onDraftChange={onDraftChange} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RatingRowProps {
  rating: RatingWithEffectiveDebits;
  draft: RatingSelectionDraft | undefined;
  isOnMember: boolean;
  disabled?: boolean;
  onToggle: (rating: RatingWithEffectiveDebits, checked: boolean) => void;
  onDraftChange: (ratingId: number, draft: RatingSelectionDraft) => void;
}

/**
 * Memoized so a click re-renders one row, not all of them: Diabetes Mellitus has 317 ratings and each row carries five
 * inputs, and re-rendering 1,600 controls per checkbox made every click take a quarter of a second. The parent keeps
 * `onToggle` and `onDraftChange` stable for the same reason.
 */
const RatingRow = memo(function RatingRow({ rating, draft, isOnMember, disabled, onToggle, onDraftChange }: RatingRowProps) {
  const isChecked = draft !== undefined;
  const current = draft ?? EMPTY_SELECTION_DRAFT;
  const debits = rating.genericSubstitute?.effectiveDebits ?? rating.effectiveDebits;
  const controlsDisabled = disabled || !isChecked;
  const shortName = rating.ratingText.replace("Rx : ", "");

  return (
    <tr className={`border-b border-gray-100 align-middle ${isChecked ? "bg-brand-green-05" : isOnMember ? "text-gray-500" : ""}`}>
      <td className="px-1 py-1 text-center">
        <CheckboxInput id={`rating-${rating.ratingId}`} label={`Select ${rating.ratingText}`} hideLabel checked={isChecked} disabled={disabled || isOnMember} title={isOnMember ? ON_MEMBER_TOOLTIP : undefined} onChange={(checked) => onToggle(rating, checked)} />
      </td>
      <td className="px-2 py-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-gray-800">{rating.ratingText}</span>
          {isOnMember && <TagPill label="On member" tone="navy" />}
          {rating.isMedication && <TagPill label="Rx" />}
          {rating.isCustom && <TagPill label="Custom" tone="green" />}
          {rating.specialtyDrug && <TagPill label={rating.specialtyDrug.isPapEligible ? "Aurora · PAP eligible" : "Aurora"} tone="warning" />}
          <span className="text-xs tabular-nums text-gray-500" title="Historical count of times this rating was assigned across all groups">
            {rating.selectionCount} selections
          </span>
        </span>
        {rating.indications.length > 0 && <span className="block text-xs text-gray-500">FDA indications: {rating.indications.join("; ")}</span>}
      </td>
      <td className="px-2 py-1 text-gray-600">{rating.time ?? "–"}</td>
      <td className="px-2 py-1 text-right tabular-nums" title={modificationTitle(rating)}>
        <span className={rating.modification ? "font-semibold text-brand-green-dark" : ""}>{debits}</span>
        {rating.modification && <span className="sr-only"> ({modificationTitle(rating)})</span>}
      </td>
      <td className="px-1 py-1">
        <NumberInput id={`override-${rating.ratingId}`} label={`Debits override for ${shortName}`} hideLabel value={current.debitsOverride} onChange={(debitsOverride) => onDraftChange(rating.ratingId, { ...current, debitsOverride })} placeholder={String(debits)} readOnly={controlsDisabled} isOverridden={current.debitsOverride !== null} size="sm" />
      </td>
      <td className="px-1 py-1">
        <NumberInput id={`dollars-${rating.ratingId}`} label={`UW Dollars for ${shortName}`} hideLabel value={current.uwDollars} onChange={(uwDollars) => onDraftChange(rating.ratingId, { ...current, uwDollars })} readOnly={controlsDisabled} isOverridden={current.uwDollars !== null} size="sm" />
      </td>
      <td className="px-1 py-1 text-center">
        <CheckboxInput id={`irix-${rating.ratingId}`} label={`IRIX Source for ${shortName}`} hideLabel checked={current.irixSource} disabled={controlsDisabled} onChange={(irixSource) => onDraftChange(rating.ratingId, { ...current, irixSource })} />
      </td>
      <td className="px-1 py-1">
        <NoteEditor commit="close" preview="short" notes={current.notes || null} label={`notes for ${shortName}`} disabled={controlsDisabled} onChange={(notes) => onDraftChange(rating.ratingId, { ...current, notes })} />
      </td>
    </tr>
  );
});
